import React from "react";
import PropTypes from "prop-types";
import { Box, IconButton, SvgIcon, Typography } from "@mui/material";
import { Instagram, Language, X, YouTube } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import { colors } from "../../theme/colors";
import Tooltip from "../common/Tooltip";
import { buildProfileAboutData } from "./profileAboutData";

const MotionBox = motion(Box);
const PROFILE_LAYOUT_DESKTOP_MIN_PX = 1050;
const layoutUp = `@media (min-width: ${PROFILE_LAYOUT_DESKTOP_MIN_PX}px)`;

function TikTokIcon(props) {
    return (
        <SvgIcon {...props} viewBox="0 0 24 24">
            <path d="M14 3c.4 2.2 2 3.7 4.2 4v2.9c-1.5 0-2.9-.4-4.2-1.3v6.2A5.6 5.6 0 1 1 9.6 9.3a5.7 5.7 0 0 1 1.1.1v2.9a2.8 2.8 0 1 0 2 2.7V3h1.3Z" />
        </SvgIcon>
    );
}

TikTokIcon.propTypes = {
    fontSize: PropTypes.string,
};

const SOCIAL_LINK_CONFIG = [
    { key: "website", label: "Website", Icon: Language },
    { key: "instagram", label: "Instagram", Icon: Instagram },
    { key: "tiktok", label: "TikTok", Icon: TikTokIcon },
    { key: "twitter", label: "X (Twitter)", Icon: X },
    { key: "youtube", label: "YouTube", Icon: YouTube },
];

const SOCIAL_BUTTON_SX = {
    width: "2.55rem",
    height: "2.55rem",
    borderRadius: "50%",
    border: `1px solid ${alpha(colors.primary.main, 0.14)}`,
    color: colors.text.muted,
    background: `linear-gradient(145deg, ${alpha(colors.primary.main, 0.09)}, rgba(255,255,255,0.025))`,
    boxShadow: `inset 0 1px 0 ${alpha("#ffffff", 0.04)}`,
    transition: "transform 180ms ease, color 180ms ease, border-color 180ms ease, background-color 180ms ease, box-shadow 180ms ease",
    "&:hover": {
        color: colors.primary.main,
        borderColor: alpha(colors.primary.main, 0.34),
        backgroundColor: alpha(colors.primary.main, 0.1),
        transform: "translateY(-2px)",
        boxShadow: `0 7px 18px ${alpha(colors.primary.main, 0.12)}, inset 0 1px 0 ${alpha("#ffffff", 0.06)}`,
    },
    "&:focus-visible": {
        outline: `2px solid ${alpha(colors.primary.main, 0.72)}`,
        outlineOffset: "3px",
    },
    "& .MuiSvgIcon-root": {
        fontSize: "1.18rem",
    },
};

export default function ProfileAboutSection({ profileData }) {
    const { bio, links } = buildProfileAboutData(profileData);
    const visibleLinks = SOCIAL_LINK_CONFIG.filter(({ key }) => links[key]);

    if (!bio && visibleLinks.length === 0) return null;

    return (
        <MotionBox
            component="section"
            aria-labelledby="profile-about-title"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1], delay: 0.16 }}
            sx={{
                position: "relative",
                mt: "0.35rem",
                mb: "0.55rem",
                pt: "1.2rem",
                px: "0.15rem",
                pb: "0.45rem",
                borderTop: "1px solid rgba(255,255,255,0.05)",
                [layoutUp]: {
                    mt: "0.4rem",
                    mb: 0,
                    pb: "0.25rem",
                },
            }}
        >
            <Typography
                id="profile-about-title"
                component="h2"
                sx={{
                    m: 0,
                    color: colors.text.primary,
                    fontSize: "1.05rem",
                    fontWeight: 700,
                    lineHeight: 1.25,
                    letterSpacing: "0.01em",
                }}
            >
                About
            </Typography>

            {bio ? (
                <Typography
                    sx={{
                        mt: "0.7rem",
                        color: colors.text.secondary,
                        fontSize: "0.82rem",
                        fontWeight: 400,
                        lineHeight: 1.65,
                        letterSpacing: "0.01em",
                        whiteSpace: "pre-line",
                        overflowWrap: "anywhere",
                    }}
                >
                    {bio}
                </Typography>
            ) : null}

            {visibleLinks.length > 0 ? (
                <Box
                    aria-label="Social links"
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: "0.65rem",
                        mt: bio ? "0.95rem" : "0.8rem",
                    }}
                >
                    {visibleLinks.map(({ key, label, Icon }) => (
                        <Tooltip key={key} title={label} placement="top">
                            <IconButton
                                className="social-btn"
                                component="a"
                                href={links[key]}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={label}
                                disableRipple
                                sx={SOCIAL_BUTTON_SX}
                            >
                                <Icon aria-hidden />
                            </IconButton>
                        </Tooltip>
                    ))}
                </Box>
            ) : null}
        </MotionBox>
    );
}

ProfileAboutSection.propTypes = {
    profileData: PropTypes.object,
};
